/**
 * Center `child` inside a horizontal scroller without Element.scrollIntoView.
 * On mobile WebKit/Chrome, scrollIntoView({ inline: 'center' }) often scrolls the
 * document (or a transformed ancestor) and leaves tab content clipped until refresh.
 */
export function scrollChildIntoHorizontalScroller(
  scroller: HTMLElement,
  child: HTMLElement,
  behavior: ScrollBehavior = 'smooth'
): boolean {
  const scrollerRect = scroller.getBoundingClientRect();
  const childRect = child.getBoundingClientRect();
  if (scrollerRect.width <= 0) return false;

  const delta =
    childRect.left + childRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);
  const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  const next = Math.max(0, Math.min(maxScroll, scroller.scrollLeft + delta));
  if (Math.abs(next - scroller.scrollLeft) < 1) return false;

  scroller.scrollTo({ left: next, behavior });
  return true;
}

/** Run `cb` when a smooth horizontal scroll finishes (scrollend + timeout fallback). */
export function afterHorizontalScrollSettles(scroller: HTMLElement, cb: () => void): () => void {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    cb();
  };
  const t = window.setTimeout(finish, 420);
  scroller.addEventListener('scrollend', finish, { once: true });
  return () => {
    done = true;
    window.clearTimeout(t);
    scroller.removeEventListener('scrollend', finish);
  };
}

/**
 * One layout read now + one after paint. Avoid multi-timeout height sync — each
 * setState re-renders the full tab carousel and makes clicks/transitions feel laggy.
 * ResizeObserver should cover late-loading content.
 */
export function schedulePaneHeightSync(read: () => void): () => void {
  let cancelled = false;
  const run = () => {
    if (!cancelled) read();
  };

  run();
  const raf = window.requestAnimationFrame(run);

  return () => {
    cancelled = true;
    window.cancelAnimationFrame(raf);
  };
}

/** Shared easing for profile / comments tab carousels. */
export const TAB_CAROUSEL_TRANSITION = 'transform 0.28s cubic-bezier(0.22, 1, 0.36, 1)';
export const TAB_INDICATOR_TRANSITION =
  'left 0.28s cubic-bezier(0.22, 1, 0.36, 1), width 0.28s cubic-bezier(0.22, 1, 0.36, 1)';

/**
 * Position a tab underline with inline left/width (DOM-only, not React state).
 * Inline styles stay visible even if the injected profile stylesheet is stale from HMR.
 */
export function applyTabIndicator(
  el: HTMLElement | null | undefined,
  left: number,
  width: number,
  animate: boolean
): void {
  if (!el) return;
  el.style.transition = animate ? TAB_INDICATOR_TRANSITION : 'none';
  el.style.left = `${Math.max(0, left)}px`;
  el.style.width = `${Math.max(0, width)}px`;
}

/**
 * Keep the finger-drag visual continuous when committing a swipe to the next tab:
 * set a compensating --drag-x so changing the % index does not jump, then ease to 0.
 */
export function settleCarouselDrag(options: {
  track: HTMLElement;
  dragVar: string;
  fromCarouselIndex: number;
  toCarouselIndex: number;
  offsetPx: number;
  paneWidthPx: number;
  onSettling?: () => void;
  onReleased?: () => void;
}): void {
  const {
    track,
    dragVar,
    fromCarouselIndex,
    toCarouselIndex,
    offsetPx,
    paneWidthPx,
    onSettling,
    onReleased
  } = options;
  const compensate = offsetPx + (toCarouselIndex - fromCarouselIndex) * paneWidthPx;
  track.style.transition = 'none';
  track.style.setProperty(dragVar, `${compensate}px`);
  onSettling?.();
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      track.style.transition = TAB_CAROUSEL_TRANSITION;
      track.style.setProperty(dragVar, '0px');
      onReleased?.();
    });
  });
}
